import { useEffect } from 'react'
import type { ChangeEvent, KeyboardEvent } from 'react'

import type { Theme } from '@emotion/react'
import { useDebounce } from 'use-debounce'

import { pushToDataLayer } from '@twisto/base'
import { IconButton } from '@twisto/components/atoms/button'
import { Input } from '@twisto/components/atoms/input'
import { CloseIcon, SearchIcon } from '@twisto/icons'

import * as styles from './search-bar.styles'
import type { Sizes } from './search-bar.styles'

type Props = {
  searchQuery: string
  setSearchQuery: (newQuery: string) => void
  size?: Sizes
  name: string
  placeholder?: string
  disabled?: boolean
  autocomplete?: string
  onBlur?: () => void
  onFocus?: () => void
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
}

const debounceTimer = 1500

export const SearchBar = ({
  searchQuery,
  setSearchQuery,
  size = 'large',
  name,
  placeholder,
  disabled = false,
  autocomplete,
  onBlur,
  onFocus,
  onKeyDown,
}: Props) => {
  const [debouncedSearchQuery] = useDebounce(searchQuery, debounceTimer)

  useEffect(() => {
    if (debouncedSearchQuery !== '') {
      pushToDataLayer({
        event: 'search',
        // eslint-disable-next-line camelcase
        search_term: debouncedSearchQuery,
      })
    }
  }, [debouncedSearchQuery])

  return (
    <Input
      autoComplete={autocomplete}
      border={false}
      data-testid="search-bar"
      disabled={disabled}
      endAdornment={
        searchQuery ? (
          <IconButton
            color="neutral"
            icon={CloseIcon}
            size="small"
            onClick={() => setSearchQuery('')}
          />
        ) : undefined
      }
      name={name}
      placeholder={placeholder}
      startAdornment={<SearchIcon />}
      styles={{
        root: styles.inputWrapper,
        input: [
          styles.input,
          styles.sizes[size],
          disabled && styles.disabledInput,
        ],
        startAdornment: (theme: Theme) => [
          styles.inputIcon(theme, size),
          disabled && styles.disabledInputIcon,
        ],
        endAdornment: styles.endAdornment,
      }}
      value={searchQuery}
      onBlur={onBlur}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        setSearchQuery(event.target.value)
      }
      onFocus={onFocus}
      onKeyDown={onKeyDown}
    />
  )
}
